// src/base-fields.ts
import { z } from "jazz-tools";
var subscriptionSettingsFields = {
  /** Current subscription tier */
  subscriptionTier: z.optional(z.enum(["free", "plus", "premium", "enterprise"])),
  /** Subscription status */
  subscriptionStatus: z.optional(z.enum(["active", "past_due", "cancelled", "trialing", "beta"])),
  /** When the current subscription period ends (Unix timestamp) */
  subscriptionEndsAt: z.optional(z.number()),
  /** Cached max items limit for offline display */
  maxItems: z.optional(z.number()),
  /** Cached retention days for offline display */
  retentionDays: z.optional(z.number()),
  /** Last time subscription was synced from backend (Unix timestamp) */
  subscriptionSyncedAt: z.optional(z.number())
};

// src/constants.ts
var DEFAULT_TIER_LIMITS = {
  free: { maxItems: 3, retentionDays: 7 },
  plus: { maxItems: 30, retentionDays: 30 },
  premium: { maxItems: 300, retentionDays: 365 },
  enterprise: { maxItems: -1, retentionDays: -1 }
  // -1 = unlimited
};
function normalizeRawLimits(raw) {
  return {
    maxItems: raw.maxItems === -1 ? Infinity : raw.maxItems,
    retentionDays: raw.retentionDays === -1 ? Infinity : raw.retentionDays
  };
}

// src/display.ts
function getTierDisplayName(tier) {
  const names = {
    free: "Free",
    plus: "Plus",
    premium: "Premium",
    enterprise: "Enterprise"
  };
  return names[tier];
}
function isPaidTier(tier) {
  return tier !== "free";
}

// src/limits.ts
function getSubscriptionLimits(tier) {
  const raw = DEFAULT_TIER_LIMITS[tier] ?? DEFAULT_TIER_LIMITS.free;
  return normalizeRawLimits(raw);
}
function getEffectiveTier(tier, status) {
  if (status === "beta") {
    return "plus";
  }
  if (status === "past_due" || status === "cancelled") {
    return "free";
  }
  return tier;
}
function canCreateItem(currentCount, tier, status) {
  const effectiveTier = getEffectiveTier(tier, status);
  const limits = getSubscriptionLimits(effectiveTier);
  return currentCount < limits.maxItems;
}
function getItemsRemaining(currentCount, tier, status) {
  const effectiveTier = getEffectiveTier(tier, status);
  const limits = getSubscriptionLimits(effectiveTier);
  if (limits.maxItems === Infinity) return Infinity;
  return Math.max(0, limits.maxItems - currentCount);
}
function shouldPurge(archivedAt, tier, status) {
  if (!archivedAt) return false;
  const effectiveTier = getEffectiveTier(tier, status);
  const limits = getSubscriptionLimits(effectiveTier);
  if (limits.retentionDays === Infinity) return false;
  const now = /* @__PURE__ */ new Date();
  const ageMs = now.getTime() - archivedAt.getTime();
  const ageDays = ageMs / (1e3 * 60 * 60 * 24);
  return ageDays > limits.retentionDays;
}
function getUsagePercentage(currentCount, tier, status) {
  const effectiveTier = getEffectiveTier(tier, status);
  const limits = getSubscriptionLimits(effectiveTier);
  if (limits.maxItems === Infinity) return 0;
  const percentage = currentCount / limits.maxItems * 100;
  return Math.min(100, Math.round(percentage));
}
export {
  DEFAULT_TIER_LIMITS,
  canCreateItem,
  getEffectiveTier,
  getItemsRemaining,
  getSubscriptionLimits,
  getTierDisplayName,
  getUsagePercentage,
  isPaidTier,
  normalizeRawLimits,
  shouldPurge,
  subscriptionSettingsFields
};
